import { useId } from "react";
import type { Location } from "@reno-estimator/core";
import { StepShell } from "../ui/StepShell.js";
import { PROVINCE_OPTIONS } from "../../lib/provinces.js";
import type { EstimatorFlow } from "../../state/useEstimatorFlow.js";
import { getAnalyticsClient } from "../../engine/analyticsClient.js";

export function Step5Location({ flow }: { flow: EstimatorFlow }) {
  const provinceId = useId();
  const cityId = useId();
  const postalId = useId();
  const location = (flow.getAnswer("location") as Location | undefined) ?? { province: undefined as never };

  function updateLocation(patch: Partial<Location>) {
    flow.setAnswer("location", { ...location, ...patch });
  }

  return (
    <StepShell
      title="Where is the project located?"
      helpText="Used to apply regional pricing for your area. City and postal code are optional but sharpen the estimate."
      onBack={flow.goBack}
      onNext={flow.goNext}
      nextDisabled={!location.province}
    >
      <div className="form-grid">
        <label className="text-field" htmlFor={provinceId}>
          <span>Province / territory</span>
          <select
            id={provinceId}
            value={location.province ?? ""}
            onChange={(e) => {
              const province = e.target.value as Location["province"];
              updateLocation({ province });
              getAnalyticsClient().track("province_selected", { province });
            }}
          >
            <option value="" disabled>
              Select one
            </option>
            {PROVINCE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-field" htmlFor={cityId}>
          <span>City (optional)</span>
          <input
            id={cityId}
            type="text"
            autoComplete="address-level2"
            value={location.city ?? ""}
            onChange={(e) => updateLocation({ city: e.target.value || undefined })}
          />
        </label>

        <label className="text-field" htmlFor={postalId}>
          <span>Postal code (optional)</span>
          <input
            id={postalId}
            type="text"
            autoComplete="postal-code"
            placeholder="A1A 1A1"
            value={location.postalCode ?? ""}
            onChange={(e) => updateLocation({ postalCode: e.target.value || undefined })}
          />
        </label>
      </div>
    </StepShell>
  );
}
